import { Box } from '@wix/design-system';
import classnames from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import SearchInput from '../../layouts/SearchInput';
import { CoreDomainSearchModule } from '../../module/module';
import { InlineToolbar } from '../InlineToolbar';
import { ModalToolbar } from '../ModalToolbar';
import s from './DomainSearchBar.scss';
import { getDefaultKeywords } from './DomainSearchBar.utils';
const randomKeywords = getDefaultKeywords();
const TLDS_LIMIT = 6;
const noKeywords = [];
export const DomainSearchBar = _ref => {
  var _keywordsData$keyword;
  let showOptionsIfEmptyInput = _ref.showOptionsIfEmptyInput,
    hideUnassignedDomains = _ref.hideUnassignedDomains,
    onFiltersChange = _ref.onFiltersChange,
    placeholder = _ref.placeholder,
    onSubmit = _ref.onSubmit,
    autoFocus = _ref.autoFocus,
    compact = _ref.compact,
    useModalToolbar = _ref.useModalToolbar,
    _ref$multiline = _ref.multiline,
    multiline = _ref$multiline === void 0 ? useModalToolbar : _ref$multiline,
    onFocus = _ref.onFocus,
    onClear = _ref.onClear,
    onBlur = _ref.onBlur,
    showSearchIcon = _ref.showSearchIcon,
    submitIcon = _ref.submitIcon,
    emphasized = _ref.emphasized,
    hideClearButton = _ref.hideClearButton,
    hideFilters = _ref.hideFilters;
  const selectedKeywords = CoreDomainSearchModule.SDK.domainInputStore.useKeywords();
  const selectedTlds = CoreDomainSearchModule.SDK.domainInputStore.useTlds();
  const selectedStyle = CoreDomainSearchModule.SDK.domainInputStore.useStyle();
  const _CoreDomainSearchModu = CoreDomainSearchModule.SDK.Layouts,
    SearchContainer = _CoreDomainSearchModu.SearchContainer,
    StatusContainer = _CoreDomainSearchModu.StatusContainer,
    StatusComponent = _CoreDomainSearchModu.StatusComponent;
  const component = CoreDomainSearchModule.internals.dataHooks.component;
  const _CoreDomainSearchModu2 = CoreDomainSearchModule.internals.useTranslations(),
    t = _CoreDomainSearchModu2.t;
  const _useState = useState(false),
    isFocused = _useState[0],
    setIsFocused = _useState[1];
  const keywordsFallback = useMemo(() => randomKeywords(t), [t]);
  useEffect(() => {
    if (isFocused) {
      onFocus == null || onFocus();
    } else {
      onBlur == null || onBlur();
    }
  }, [isFocused, onBlur, onFocus]);
  const setQuery = CoreDomainSearchModule.SDK.domainInputStore.setQuery;
  const _CoreDomainSearchModu3 = CoreDomainSearchModule.internals.backend.hooks.useGetKeywords({
      limit: 8 - ((selectedKeywords == null ? void 0 : selectedKeywords.length) ?? 0)
    }, true, {
      cacheTime: Infinity,
      staleTime: Infinity,
      useErrorBoundary: false,
      suspense: false,
      retry: false
    }),
    keywordsData = _CoreDomainSearchModu3.data,
    keywordsError = _CoreDomainSearchModu3.error;
  const _CoreDomainSearchModu4 = CoreDomainSearchModule.internals.backend.hooks.useGetTldSelectorData({
      limit: TLDS_LIMIT
    }, true, {
      cacheTime: Infinity,
      staleTime: Infinity,
      useErrorBoundary: false,
      suspense: false,
      retry: false
    }),
    tldsData = _CoreDomainSearchModu4.data;
  const availableKeywords = keywordsError || !(keywordsData != null && (_keywordsData$keyword = keywordsData.keywords) != null && _keywordsData$keyword.length) ? keywordsFallback.keywords ?? noKeywords : keywordsData.keywords ?? noKeywords;
  const concatenatedKeywords = useMemo(() => {
    if (useModalToolbar) {
      // Maintaining stable order
      const availableKeywordSet = new Set(availableKeywords);
      const stableKeywords = [...availableKeywords, ...selectedKeywords.filter(k => !availableKeywordSet.has(k))];
      return stableKeywords;
    }
    const keywordSet = new Set([...(selectedKeywords || []), ...(availableKeywords || [])]);
    return Array.from(keywordSet);
  }, [availableKeywords, selectedKeywords, useModalToolbar]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    align: "center"
  }, /*#__PURE__*/React.createElement(SearchContainer, {
    dataHook: component,
    className: classnames(s.domainSearchBar, {
      [s.compact]: compact,
      [s.hasFocus]: isFocused,
      [s.emphasized]: emphasized
    })
  }, /*#__PURE__*/React.createElement(SearchInput, {
    showSearchIcon: showSearchIcon,
    submitIcon: submitIcon,
    emphasized: emphasized,
    hideClearButton: hideClearButton,
    autoFocus: autoFocus,
    multiline: multiline,
    placeholder: placeholder ?? [t('core_domain_search.domain_gate.search_field.placeholder.domain_name'), t('core_domain_search.domain_gate.search_field.placeholder.free_text')],
    showOptionsIfEmptyInput: showOptionsIfEmptyInput,
    hideUnassignedDomains: hideUnassignedDomains,
    onChange: setQuery,
    onClear: onClear,
    onSubmit: filters => {
      const hasActiveFilters = selectedTlds.length > 0 || selectedKeywords.length > 0 || Boolean(selectedStyle);
      if (!useModalToolbar || !hasActiveFilters) {
        setIsFocused(false);
      }
      onSubmit == null || onSubmit(filters);
    },
    onFocus: () => setIsFocused(true)
  }), useModalToolbar ? /*#__PURE__*/React.createElement(ModalToolbar, {
    isFocused: hideFilters ? false : isFocused,
    availableTlds: (tldsData == null ? void 0 : tldsData.tldSelectorData) ?? [],
    concatenatedKeywords: concatenatedKeywords,
    onFiltersChange: onFiltersChange
  }) : /*#__PURE__*/React.createElement(InlineToolbar, {
    compact: compact,
    isFocused: hideFilters ? false : isFocused,
    availableTlds: (tldsData == null ? void 0 : tldsData.tldSelectorData) ?? [],
    concatenatedKeywords: concatenatedKeywords,
    onFiltersChange: onFiltersChange
  })), StatusComponent ? /*#__PURE__*/React.createElement(StatusContainer, null, /*#__PURE__*/React.createElement(StatusComponent, null)) : null);
};
export default DomainSearchBar;
//# sourceMappingURL=DomainSearchBar.js.map